import { FormEvent, useDeferredValue, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { PostSearchBlock } from '@/types/blocks';

interface PublicPostSearchProps {
  block: PostSearchBlock;
  siteId: string;
  siteSlug: string;
}

interface SearchResult {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
  has_more: boolean;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Busca sob demanda: não faz consulta alguma enquanto o visitante não enviar o termo. */
export function PublicPostSearch({ block, siteId, siteSlug }: PublicPostSearchProps) {
  const [input, setInput] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState('');
  const term = useDeferredValue(submittedTerm.trim());

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isError } = useInfiniteQuery({
    queryKey: ['public-post-search', siteId, term],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<SearchResult[]> => {
      const { data, error } = await supabase
        .rpc('search_public_posts_page', {
          input_site_id: siteId,
          input_query: term,
          input_offset: pageParam,
        });
      if (error) throw error;
      return data ?? [];
    },
    getNextPageParam: (lastPage, allPages) => lastPage[0]?.has_more ? allPages.flat().length : undefined,
    enabled: Boolean(term),
    staleTime: 60_000,
  });
  const results = data?.pages.flat() ?? [];
  const textColor = block.textColor || 'var(--color-paragraph, #475569)';
  const titleColor = block.textColor || 'var(--color-heading, #0f172a)';
  const fontFamily = block.fontFamily || 'var(--font-paragraph, var(--font-body))';
  const fontSize = block.fontSize && block.fontSize > 0 ? `${block.fontSize}px` : undefined;
  const textStyle = { color: textColor, fontFamily, fontSize };
  const inputOpacity = Math.max(0, Math.min(100, block.inputBackgroundOpacity ?? 100));
  const inputBackground = block.inputBackgroundColor === 'transparent'
    ? 'transparent'
    : `color-mix(in srgb, ${block.inputBackgroundColor || 'var(--color-box-bg, #fff)'} ${inputOpacity}%, transparent)`;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittedTerm(input);
  };

  return (
    <section className="w-full py-10">
      <div className="mx-auto max-w-3xl px-6">
        <div className="rounded-lg border p-6 shadow-sm" style={{ backgroundColor: block.backgroundColor || 'var(--color-box-bg, #fff)', borderColor: 'color-mix(in oklab, var(--color-primary), transparent 82%)' }}>
          {block.title && <h2 className="text-center text-2xl font-bold" style={{ color: titleColor, fontFamily: block.fontFamily || 'var(--font-heading, var(--font-display))', fontSize }}>{block.title}</h2>}
          {block.description && <p className="mx-auto mt-2 max-w-xl text-center text-sm" style={textStyle}>{block.description}</p>}
          <form className="mt-5 flex flex-col gap-2 sm:flex-row" onSubmit={submit}>
            <label className="sr-only" htmlFor={`post-search-${block.id}`}>{block.placeholder || 'Buscar posts'}</label>
            <input
              id={`post-search-${block.id}`}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={block.placeholder || 'Digite o que você procura'}
              className="min-w-0 flex-1 rounded-lg border bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--color-accent)]"
              style={{ borderColor: 'color-mix(in oklab, var(--color-primary), transparent 74%)', backgroundColor: inputBackground, color: titleColor, fontFamily, fontSize }}
            />
            <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-opacity hover:opacity-90" style={{ backgroundColor: block.buttonColor || 'var(--color-primary, #0f172a)', color: block.buttonTextColor || '#fff' }}>
              <Search className="h-4 w-4" /> {block.buttonLabel || 'Buscar'}
            </button>
          </form>
          {term && (
            <div className="mt-5" aria-live="polite">
              {isFetching && !isFetchingNextPage ? (
                <p className="text-center text-sm" style={textStyle}>Buscando posts...</p>
              ) : isError ? (
                <p className="text-center text-sm text-rose-600">Não foi possível realizar a busca agora. Tente novamente.</p>
              ) : results.length === 0 ? (
                <p className="text-center text-sm" style={textStyle}>{block.emptyMessage || 'Nenhum post encontrado para esta busca.'}</p>
              ) : (
                <ul className="divide-y" style={{ borderColor: 'color-mix(in oklab, var(--color-primary), transparent 88%)' }}>
                  {results.map((result) => (
                    <li key={result.id}>
                      <Link to={`/s/${siteSlug}/${result.slug}`} className="block py-4 transition-opacity hover:opacity-70">
                        <p className="font-semibold" style={{ color: titleColor, fontFamily, fontSize }}>{result.title}</p>
                        {result.excerpt && <p className="mt-1 line-clamp-2 text-sm" style={textStyle}>{result.excerpt}</p>}
                        {formatDate(result.published_at) && <p className="mt-2 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-accent, #b45309)' }}>{formatDate(result.published_at)}</p>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {!isError && results.length > 0 && hasNextPage && (
                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={() => void fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="rounded-lg border px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-75 disabled:cursor-wait disabled:opacity-60"
                    style={{ borderColor: 'var(--color-primary, #0f172a)', color: 'var(--color-primary, #0f172a)' }}
                  >
                    {isFetchingNextPage ? 'Carregando...' : 'Carregar mais resultados'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
